import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

const HERO_IMAGE_SRC = '/Generated_Image_April_10,_2026_-_5_33PM.jpg';

export default function HeroSection() {
  const [heroReady, setHeroReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const img = new Image();
    img.src = HERO_IMAGE_SRC;
    img.decoding = 'async';
    const done = () => {
      if (!cancelled) setHeroReady(true);
    };
    if (img.decode) {
      img.decode().then(done).catch(done);
    } else if (img.complete) {
      done();
    } else {
      img.onload = done;
      img.onerror = done;
    }
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section className="pt-24 sm:pt-28 pb-14 sm:pb-20 px-4 sm:px-6 overflow-hidden relative" style={{ backgroundColor: '#1a2332' }}>
      <div className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(to bottom, transparent 0%, transparent 50%, #060b14 100%)' }} />
      <div className="max-w-5xl mx-auto relative z-10">
        <div className="relative mb-6 sm:mb-10 flex justify-center -mx-4 sm:-mx-6">
          <div className="relative w-full max-w-5xl" style={{ aspectRatio: '16 / 9' }}>
            <img
              src={HERO_IMAGE_SRC}
              alt="BONESPAY"
              className="w-full absolute inset-0"
              loading="eager"
              decoding="async"
              fetchPriority="high"
              style={{
                display: 'block',
                opacity: heroReady ? 1 : 0,
                transition: 'opacity 300ms ease-out',
                maskImage: `
                  linear-gradient(to right, transparent, black 12%, black 88%, transparent),
                  linear-gradient(to bottom, transparent, black 10%, black 82%, transparent)
                `,
                maskComposite: 'intersect',
                WebkitMaskImage: `
                  linear-gradient(to right, transparent, black 12%, black 88%, transparent),
                  linear-gradient(to bottom, transparent, black 10%, black 82%, transparent)
                `,
                WebkitMaskComposite: 'source-in',
              }}
            />
          </div>
        </div>

        <div className="mb-8 sm:mb-10 text-center">
          <h1 className="font-serif text-bone-white text-3xl sm:text-5xl md:text-6xl lg:text-7xl leading-tight mb-5 sm:mb-6">
            Process Crypto Orders Natively.
          </h1>

          <p className="font-sans text-navy-200/80 text-sm sm:text-base md:text-lg max-w-2xl mx-auto mb-8 sm:mb-10 leading-relaxed px-2">
            We provide merchants with the technology they need to seamlessly process crypto payments on their merchant websites.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mb-10 sm:mb-12">
            <Link
              to="/apply"
              className="inline-flex items-center gap-2 font-mono text-sm font-medium bg-bone-white text-navy-900 px-8 py-3 rounded-subtle hover:bg-navy-100 transition-colors uppercase tracking-wider group w-full sm:w-auto justify-center"
            >
              Apply Now
              <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <a
              href="#how-it-works"
              className="font-mono text-sm text-bone-white/50 hover:text-bone-white transition-colors uppercase tracking-wider px-2 py-3"
              onClick={(e) => {
                e.preventDefault();
                document.querySelector('#how-it-works')?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              See how it works
            </a>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mt-6 sm:mt-8">
          {[
            { label: 'Settlement', value: 'USDC on Solana' },
            { label: 'Custody', value: 'Non-Custodial Wallet' },
            { label: 'Verification', value: 'On-Chain + HMAC' },
          ].map(({ label, value }) => (
            <div key={label} className="border border-navy-700/30 rounded-subtle p-4 text-center">
              <div className="font-mono text-[9px] sm:text-[10px] text-bone-white/30 uppercase tracking-widest mb-1.5">{label}</div>
              <div className="font-sans text-sm sm:text-base text-bone-white font-medium">{value}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
